<script setup lang="ts">
import EstateThumbHorizontal from '../thumb/EstateThumbHorizontal.vue'
import EstateListingItemBanner from './EstateListingItemBanner.vue'
import Pagination from '~/components/global/Pagination.vue'
import type { useEstateListingMap } from '~/composables/estateListingMap/useEstateListingMap'
import { usePaginationState } from '~/composables/pagination/usePaginationState'
import type {
  EstateListingResponse,
  EstateType,
} from '~/types/api/EstateListing'

const props = defineProps<{
  officesResponse: ReturnType<typeof useAsyncData<EstateListingResponse | null>>
  estateType: EstateType
  estateListingMap: ReturnType<typeof useEstateListingMap>
  paginationState: ReturnType<typeof usePaginationState>
}>()

const estateListingItemsMainRef = ref<HTMLElement | null>(null)

props.paginationState.actions.setOnPageChangeCallback(() => {
  if (!estateListingItemsMainRef.value) return

  estateListingItemsMainRef.value.scrollTo({
    top: 0,
    behavior: 'smooth',
  })
})
</script>

<template>
  <div class="c-estate-listing-items">
    <div class="c-estate-listing-items__head">
      <h1 class="c-estate-listing-items__title">
        {{ $t(`pages.estateListing.header.titles.${estateType}`) }}
      </h1>

      <i18n-t
        v-if="officesResponse.data.value?.count !== undefined"
        keypath="pages.estateListing.header.subtitle.baseFiltered"
        tag="div"
        scope="global"
        class="c-estate-listing-items__subtitle"
      >
        <template #_0>
          <strong>{{
            $t(
              'pages.estateListing.header.subtitle.0',
              { _0: officesResponse.data.value.count },
              officesResponse.data.value.count
            )
          }}</strong>
        </template>
      </i18n-t>

      <div v-else class="c-estate-listing-items__subtitle is-skeleton" />
    </div>

    <div ref="estateListingItemsMainRef" class="c-estate-listing-items__main">
      <template
        v-if="
          officesResponse.pending.value && !officesResponse.data.value?.data
        "
      >
        <div class="c-estate-listing-items__items is-skeleton">
          <div
            v-for="index in 4"
            :key="index"
            class="c-estate-listing-items__item is-skeleton"
          ></div>
        </div>
      </template>

      <template v-else-if="officesResponse.data.value?.data">
        <div
          :class="[
            'c-estate-listing-items__items',
            officesResponse.pending.value && 'is-loading',
          ]"
        >
          <template
            v-for="(estateDataItem, index) in officesResponse.data.value.data"
            :key="estateDataItem.id"
          >
            <EstateListingItemBanner
              v-if="index === 7"
              :estate-type="estateType"
            />

            <div class="c-estate-listing-items__item">
              <EstateThumbHorizontal
                :estate-data="estateDataItem"
                :estate-type="estateType"
              />
            </div>
          </template>

          <IAlert
            v-if="officesResponse.data.value?.count === 0"
            color="info"
            class="c-estate-listing-items__item is-empty"
          >
            {{ $t('pages.estateListing.emptyMessage') }}
          </IAlert>

          <EstateListingItemBanner
            v-if="officesResponse.data.value?.count < 7"
            :estate-type="estateType"
          />
        </div>

        <div
          v-if="officesResponse.data.value?.count > 12"
          class="c-estate-listing-items__pagination"
        >
          <Pagination :pagination-state="paginationState" />
        </div>
      </template>
    </div>
  </div>
</template>

<style lang="scss" scoped>
@use '@inkline/inkline/css/mixins' as *;
@use '@/assets/styles/mixins/skeleton' as skeleton;

.c-estate-listing-items {
  min-height: 0;
  max-height: inherit;
  display: flex;
  flex-direction: column;

  @include breakpoint-down('lg') {
    display: none;
  }

  &__head {
    position: sticky;
    top: 0;
    padding: 36px 40px 12px 64px;
    background-color: var(--color-light);
    z-index: 2;
    flex: 0 0 auto;
  }

  &__main {
    flex: 1 1 auto;
    min-height: 0;
    max-height: inherit;
    overflow: scroll;
    display: flex;
    flex-direction: column;
    padding: 12px 40px 40px 64px;
  }

  &__items {
    display: flex;
    flex-direction: column;
    gap: 24px;
    flex: 1 1 auto;
    transition: opacity var(--transition-duration)
      var(--transition-timing-function);

    &.is-loading {
      opacity: 0.6;
    }

    &.is-skeleton {
      display: grid;
      grid-auto-rows: 1fr;
    }
  }

  &__pagination {
    margin-top: 40px;

    :deep(.c-pagination) {
      display: flex;
      flex-direction: column;
      align-items: center;
      gap: 12px;

      .c-pagination__pages {
        order: -1;
      }

      .c-pagination__more {
        display: none;
      }
    }
  }

  &__title {
    font-size: 28px;
    line-height: 0.92;
    margin-bottom: 6px;
  }

  &__subtitle {
    font-size: 16px;
    line-height: 1.48;

    strong {
      font-weight: 700;
    }

    &.is-skeleton {
      @include skeleton.skeleton;
      height: 19px;
      margin-top: 10px;
      width: 320px;
    }
  }

  &__item {
    &.is-empty {
      flex: 0 0 auto;
    }

    &.is-skeleton {
      @include skeleton.skeleton;
      display: grid;
      grid-auto-rows: 1fr;
    }
  }
}
</style>
